/** The client cache on disk: IndexedDB `xflow-cache` with the stores meta, rows and views (design 16.8). */

const NAME = "xflow-cache";
export const META = "meta";
export const ROWS = "rows";
export const VIEWS = "views";

/** One row of a collection, with the server time of the data in it. */
export interface RowRecord<T = unknown> {
  scope: string;
  collection: string;
  id: string;
  as_of: number;
  row: T;
}

/** The ids of a list page in their order, and the other fields of its answer. */
export interface ViewRecord<E = unknown> {
  scope: string;
  name: string;
  collection: string;
  at: number;
  ids: string[];
  extra: E;
}

/** The state of one scope; `cursor` is the change feed seq of the kept rows, null before the first sync. */
export interface ScopeMeta {
  scope: string;
  used_at: number;
  roles: string[] | null;
  cursor: number | null;
  /** The org-local date of the last full load; a new day loads all rows again. */
  day?: string | null;
  access_key?: string | null;
}

let opened: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  opened ??= new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      db.createObjectStore(META);
      db.createObjectStore(ROWS);
      db.createObjectStore(VIEWS);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  }).catch((error) => {
    opened = null;
    throw error;
  });
  return opened;
}

type Stores = Record<typeof META | typeof ROWS | typeof VIEWS, IDBObjectStore>;

/** Run `work` in one transaction over all three stores. */
export function run<T>(mode: IDBTransactionMode, work: (stores: Stores, done: (value: T) => void) => void): Promise<T | undefined> {
  return open().then(
    (db) =>
      new Promise<T | undefined>((resolve, reject) => {
        const tx = db.transaction([META, ROWS, VIEWS], mode);
        let result: T | undefined;
        work({ meta: tx.objectStore(META), rows: tx.objectStore(ROWS), views: tx.objectStore(VIEWS) }, (value) => {
          result = value;
        });
        tx.oncomplete = () => resolve(result);
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
      }),
  );
}

/** All keys of one scope: the array keys [scope, ...] sort before [scope, []]. */
export const scopeRange = (scope: string) => IDBKeyRange.bound([scope], [scope, []]);
export const rowKey = (scope: string, collection: string, id: string) => [scope, collection, id];
export const collectionRange = (scope: string, collection: string) => IDBKeyRange.bound([scope, collection], [scope, collection, []]);
export const viewKey = (scope: string, name: string) => [scope, name];
export const metaKey = (scope: string) => `scope:${scope}`;
export const SCHEMA_KEY = "schema";

/** For tests: forget the open connection after the IndexedDB factory changes. */
export function resetForTests(): void {
  opened = null;
}
